import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, fmtMoney, ESTATUS_TRAMITE, PRIORIDAD_STYLE } from "../lib/format";
import { Card, StatusBadge, ProgressBar, SectionTitle } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Upload, FileText, Download, History, AlertTriangle, Plus, Link2 } from "lucide-react";
import { ZonaCapturaIA } from "../components/CapturaIA";

function CampoFecha({ label, value, onSave, disabled, testId }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <Input data-testid={testId} type="date" defaultValue={value || ""} disabled={disabled} className="h-8 text-sm"
        onBlur={(e) => e.target.value !== (value || "") && onSave(e.target.value || null)} />
    </div>
  );
}

export default function TramiteDetail() {
  const { pid, tid } = useParams();
  const { canEdit, isAdmin } = useAuth();
  const [t, setT] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null); // {estatus, pendientes?}
  const [comentario, setComentario] = useState("");
  const [nuevoReq, setNuevoReq] = useState("");
  const [docNombre, setDocNombre] = useState("");
  const [docTipo, setDocTipo] = useState("Oficio");
  const [docFile, setDocFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const load = () => api.get(`/tramites/${tid}`).then((r) => setT(r.data));
  useEffect(() => { setT(null); load(); }, [tid]); // eslint-disable-line

  const patch = async (changes) => {
    try { await api.patch(`/tramites/${tid}`, changes); await load(); toast.success("Trámite actualizado"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const cambiarEstatus = async (estatus, override = false) => {
    if (!comentario.trim()) return toast.error("El comentario es obligatorio para el cambio de estatus");
    try {
      await api.post(`/tramites/${tid}/estatus`, { estatus, comentario, override });
      toast.success(`Estatus cambiado a "${estatus}"`);
      setStatusDialog(null); setComentario("");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.codigo === "dependencias_pendientes") {
        setStatusDialog({ estatus, pendientes: d.pendientes });
      } else toast.error(apiError(e));
    }
  };

  const toggleReq = async (index, completado) => {
    try { await api.post(`/tramites/${tid}/requisitos`, { index, completado }); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const agregarReq = async () => {
    if (!nuevoReq.trim()) return;
    try { await api.post(`/tramites/${tid}/requisitos/agregar`, { nombre: nuevoReq }); setNuevoReq(""); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const subirDoc = async () => {
    if (!docFile || !docNombre.trim()) return toast.error("Selecciona un archivo y asigna un nombre");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", docFile);
    fd.append("nombre", docNombre);
    fd.append("tipo_documento", docTipo);
    try {
      await api.post(`/tramites/${tid}/documentos`, fd);
      toast.success("Documento cargado al repositorio");
      setDocFile(null); setDocNombre("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setUploading(false);
  };

  const descargar = async (d) => {
    const r = await api.get(`/documentos/${d.id}/descargar`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    if (d.content_type === "application/pdf") setPreviewDoc({ ...d, url });
    else {
      const a = document.createElement("a");
      a.href = url; a.download = d.archivo_original || d.nombre; a.click();
    }
  };

  if (!t) return <div className="p-10 text-slate-400 animate-pulse">Cargando trámite...</div>;

  return (
    <div className="p-4 sm:p-8 max-w-[1100px]">
      <Link to={`/p/${pid}/tramites`} data-testid="volver-tramites" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#002B49] mb-4 transition-colors">
        <ArrowLeft size={14} /> Volver a trámites
      </Link>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-start justify-between flex-wrap gap-4 mb-2">
          <div className="max-w-2xl">
            <p className="text-xs tracking-[0.2em] uppercase text-[#00A3B5] font-bold mb-1">{t.etapa} · {t.categoria}</p>
            <h1 className="font-display text-2xl font-bold text-[#002B49]" data-testid="tramite-nombre">{t.nombre}</h1>
            <p className="text-sm text-slate-500 mt-1">
              {t.autoridad || "Sin autoridad"} {t.upe && <>· UPE <Link className="text-[#00A3B5] hover:underline" to={`/p/${pid}/mapa`}>{t.upe.clave}</Link></>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-bold px-2.5 py-1 rounded border ${PRIORIDAD_STYLE[t.prioridad]}`}>Prioridad {t.prioridad}</span>
            <StatusBadge estatus={t.estatus} testId="tramite-estatus-badge" />
          </div>
        </div>
        <div className="flex items-center gap-3 mb-6">
          <ProgressBar value={t.avance ?? 0} className="flex-1 max-w-md" />
          <span className="text-sm font-bold text-[#002B49]">{t.avance ?? 0}%</span>
        </div>
      </motion.div>

      {t.dependencias_detalle?.length > 0 && (
        <Card className="p-4 mb-6 bg-slate-50/60" testId="dependencias-card">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Link2 size={12} /> Dependencias (deben estar Aprobadas antes de ingresar)</p>
          <div className="flex flex-wrap gap-2">
            {t.dependencias_detalle.map((d) => (
              <Link key={d.id} to={`/p/${pid}/tramites/${d.id}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${d.estatus === "Aprobado" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-400"}`}>
                {d.nombre} — {d.estatus}
              </Link>
            ))}
          </div>
          {t.dependencia_texto && <p className="text-xs text-slate-400 mt-2">Nota: {t.dependencia_texto}</p>}
        </Card>
      )}
      {!t.dependencias_detalle?.length && t.dependencia_texto && t.dependencia_texto !== "—" && (
        <p className="text-xs text-slate-400 mb-6">Dependencia: {t.dependencia_texto}</p>
      )}

      {canEdit && (
        <Card className="p-5 mb-6" testId="workflow-card">
          <SectionTitle>Workflow de estatus</SectionTitle>
          <div className="flex flex-wrap gap-2">
            {ESTATUS_TRAMITE.filter((e) => e !== t.estatus && e !== "Vencido").map((e) => (
              <Button key={e} data-testid={`estatus-btn-${e}`} variant="outline" size="sm"
                onClick={() => { setComentario(""); setStatusDialog({ estatus: e }); }}
                className="text-xs border-slate-300 hover:border-[#00A3B5] hover:text-[#00A3B5] active:scale-[0.97]">
                → {e}
              </Button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">Cada cambio de estatus exige un comentario y queda registrado en la bitácora (auditoría obligatoria). "Vencido" se asigna automáticamente al superar la vigencia.</p>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5" testId="requisitos-card">
          <SectionTitle>Requisitos ({(t.requisitos || []).filter((r) => r.completado).length}/{(t.requisitos || []).length})</SectionTitle>
          <div className="space-y-2.5">
            {(t.requisitos || []).map((r, i) => (
              <label key={i} className="flex items-start gap-3 cursor-pointer group">
                <Checkbox data-testid={`requisito-check-${i}`} checked={r.completado} disabled={!canEdit}
                  onCheckedChange={(v) => toggleReq(i, !!v)} className="mt-0.5" />
                <span className={`text-sm ${r.completado ? "text-slate-400 line-through" : "text-slate-700"} group-hover:text-[#002B49] transition-colors`}>{r.nombre}</span>
              </label>
            ))}
            {(t.requisitos || []).length === 0 && <p className="text-sm text-slate-400">Sin sub-requisitos. El avance se estima por estatus.</p>}
          </div>
          {canEdit && (
            <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
              <Input data-testid="nuevo-requisito-input" placeholder="Agregar requisito / condicionante..." value={nuevoReq}
                onChange={(e) => setNuevoReq(e.target.value)} className="h-8 text-sm" onKeyDown={(e) => e.key === "Enter" && agregarReq()} />
              <Button data-testid="agregar-requisito-btn" onClick={agregarReq} size="sm" variant="outline" className="h-8"><Plus size={13} /></Button>
            </div>
          )}
        </Card>

        <Card className="p-5" testId="datos-card">
          <SectionTitle>Datos del trámite</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <CampoFecha label="Fecha de inicio" value={t.fecha_inicio} disabled={!canEdit} testId="fecha-inicio"
              onSave={(v) => patch({ fecha_inicio: v })} />
            <CampoFecha label="Ingreso a autoridad" value={t.fecha_ingreso} disabled={!canEdit} testId="fecha-ingreso"
              onSave={(v) => patch({ fecha_ingreso: v })} />
            <CampoFecha label="Fecha compromiso" value={t.fecha_compromiso} disabled={!canEdit} testId="fecha-compromiso"
              onSave={(v) => patch({ fecha_compromiso: v })} />
            <CampoFecha label="Fecha de resolución" value={t.fecha_resolucion} disabled={!canEdit} testId="fecha-resolucion"
              onSave={(v) => patch({ fecha_resolucion: v })} />
            <CampoFecha label="Vencimiento de vigencia" value={t.fecha_vencimiento} disabled={!canEdit} testId="fecha-vencimiento"
              onSave={(v) => patch({ fecha_vencimiento: v })} />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Folio / expediente</p>
              <Input data-testid="folio-input" defaultValue={t.folio} disabled={!canEdit} className="h-8 text-sm"
                onBlur={(e) => e.target.value !== t.folio && patch({ folio: e.target.value })} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Costo (derechos, MXN)</p>
              <Input data-testid="costo-input" type="number" defaultValue={t.costo} disabled={!canEdit} className="h-8 text-sm"
                onBlur={(e) => Number(e.target.value) !== t.costo && patch({ costo: Number(e.target.value) })} />
              <p className="text-xs text-slate-400 mt-1">{fmtMoney(t.costo)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Prioridad</p>
              <Select value={t.prioridad} onValueChange={(v) => patch({ prioridad: v })} disabled={!canEdit}>
                <SelectTrigger data-testid="prioridad-select" className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{["Alta", "Media", "Baja"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Gestor responsable</p>
              <Input data-testid="gestor-input" defaultValue={t.gestor} disabled={!canEdit} className="h-8 text-sm"
                onBlur={(e) => e.target.value !== t.gestor && patch({ gestor: e.target.value })} />
            </div>
            <div className="col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Notas</p>
              <Textarea data-testid="notas-input" defaultValue={t.notas} disabled={!canEdit} rows={2} className="text-sm"
                onBlur={(e) => e.target.value !== t.notas && patch({ notas: e.target.value })} />
            </div>
          </div>
        </Card>

        <Card className="p-5" testId="documentos-card">
          <SectionTitle>Repositorio documental ({t.documentos.length})</SectionTitle>
          {canEdit && (
            <div className="mb-4">
              <ZonaCapturaIA tramite={t} onChanged={load} />
              <details className="mt-2">
                <summary className="text-[11px] text-slate-400 cursor-pointer hover:text-slate-600">Captura manual (sin IA)</summary>
                <div className="border border-dashed border-slate-300 rounded-md p-4 mt-2 space-y-2">
                  <input data-testid="doc-file-input" type="file" accept=".pdf,image/*"
                    onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                    className="text-xs text-slate-500 w-full" />
                  <div className="flex gap-2">
                    <Input data-testid="doc-nombre-input" placeholder="Nombre del documento" value={docNombre}
                      onChange={(e) => setDocNombre(e.target.value)} className="h-8 text-sm flex-1" />
                    <Select value={docTipo} onValueChange={setDocTipo}>
                      <SelectTrigger className="h-8 text-sm w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Oficio", "Resolutivo", "Plano", "Escritura", "Constancia", "Pago de derechos", "Evidencia", "Otro"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="subir-doc-btn" onClick={subirDoc} disabled={uploading} size="sm"
                    className="bg-[#00A3B5] text-white gap-2 h-8 w-full"><Upload size={13} /> {uploading ? "Subiendo..." : "Subir documento"}</Button>
                </div>
              </details>
            </div>
          )}
          <div className="space-y-2">
            {t.documentos.map((d) => (
              <button key={d.id} data-testid={`doc-item-${d.id}`} onClick={() => descargar(d)}
                className="w-full flex items-center gap-3 border border-slate-200 rounded-md p-3 hover:border-[#00A3B5] transition-colors text-left">
                <FileText size={16} className="text-[#00A3B5] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{d.nombre} <span className="text-slate-400 font-normal">v{d.version}</span></p>
                  <p className="text-xs text-slate-400">{d.tipo_documento} · {d.subido_por} · {fmtDate(d.fecha)}</p>
                </div>
                <Download size={14} className="text-slate-400" />
              </button>
            ))}
            {t.documentos.length === 0 && <p className="text-sm text-slate-400">Aún no hay evidencias documentales. Este es el archivo digital central del trámite.</p>}
          </div>
        </Card>

        <Card className="p-5" testId="bitacora-card">
          <SectionTitle>Bitácora de cambios</SectionTitle>
          <div className="space-y-4 max-h-96 overflow-y-auto scrollbar-thin pr-2">
            {t.bitacora.map((b) => (
              <div key={b.id} className="flex gap-3">
                <History size={14} className="text-slate-300 mt-0.5 flex-shrink-0" />
                <div className="text-xs border-b border-slate-50 pb-3 flex-1">
                  <p className="text-slate-800 font-semibold">{b.accion}</p>
                  {(b.valor_anterior || b.valor_nuevo) && (
                    <p className="text-slate-500 mt-0.5">
                      {b.valor_anterior && <span className="line-through">{b.valor_anterior}</span>}
                      {b.valor_anterior && " → "}
                      <span className="text-[#002B49] font-semibold">{b.valor_nuevo}</span>
                    </p>
                  )}
                  {b.comentario && <p className="text-slate-500 italic mt-0.5">"{b.comentario}"</p>}
                  <p className="text-slate-400 mt-0.5">{b.usuario} · {new Date(b.timestamp).toLocaleString("es-MX")}</p>
                </div>
              </div>
            ))}
            {t.bitacora.length === 0 && <p className="text-sm text-slate-400">Sin movimientos aún.</p>}
          </div>
        </Card>
      </div>

      {/* Diálogo de cambio de estatus */}
      <Dialog open={!!statusDialog} onOpenChange={(o) => !o && setStatusDialog(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-[#002B49]">Cambiar estatus a "{statusDialog?.estatus}"</DialogTitle>
          </DialogHeader>
          {statusDialog?.pendientes && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
              <p className="font-bold flex items-center gap-2"><AlertTriangle size={14} /> Dependencias no aprobadas</p>
              <ul className="list-disc ml-5 mt-1 text-xs">{statusDialog.pendientes.map((p) => <li key={p}>{p}</li>)}</ul>
              {isAdmin ? <p className="text-xs mt-2">Como Administrador puedes aplicar un override. Quedará auditado.</p>
                : <p className="text-xs mt-2">Solo un Administrador puede hacer override de dependencias.</p>}
            </div>
          )}
          <Textarea data-testid="comentario-estatus" placeholder="Comentario obligatorio (queda en la bitácora con usuario y fecha)..."
            value={comentario} onChange={(e) => setComentario(e.target.value)} rows={3} />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setStatusDialog(null)}>Cancelar</Button>
            {statusDialog?.pendientes ? (
              isAdmin && <Button data-testid="confirmar-override-btn" onClick={() => cambiarEstatus(statusDialog.estatus, true)}
                className="bg-amber-600 hover:bg-amber-700 text-white">Override de Admin (auditado)</Button>
            ) : (
              <Button data-testid="confirmar-estatus-btn" onClick={() => cambiarEstatus(statusDialog?.estatus)}
                className="bg-[#002B49] text-white">Confirmar cambio</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Visor PDF */}
      <Dialog open={!!previewDoc} onOpenChange={(o) => !o && setPreviewDoc(null)}>
        <DialogContent className="bg-white max-w-4xl h-[85vh] flex flex-col">
          <DialogHeader><DialogTitle className="font-display text-[#002B49]">{previewDoc?.nombre}</DialogTitle></DialogHeader>
          {previewDoc && <iframe title="Visor PDF" src={previewDoc.url} className="flex-1 w-full rounded border border-slate-200" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
